import type { Href } from "expo-router";
import { router, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Image,
  LayoutChangeEvent,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  Text,
  useWindowDimensions,
  View,
  type ImageStyle,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Animated, {
  Easing,
  runOnJS,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle, G, Line, Path, Rect } from "react-native-svg";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { roleToRoute } from "../../lib/auth";
import { useAuth } from "../../lib/authContext";
import { COLORS, FONTS } from "../../lib/brand";
import { useMarketingFonts } from "../../lib/useMarketingFonts";

import { LogoMark, LogoWordmark } from "../brand/Logo";
import { PANE_THEMES } from "../brand/paneThemes";
import { Fireflies } from "./Fireflies";
import { MarketingFooter } from "./MarketingFooter";
import { Reveal } from "./Reveal";

/** Hero background — illustrated Coravu forest village (brand UI art). */
const HERO_BG = require("../../assets/hero-background.png");

/**
 * Brand greens / amber — wired to the /brand General pane (the single source
 * of truth for marketing). Editing PANE_THEMES.General / COLORS propagates here.
 */
const THEME = PANE_THEMES.General;
const FOREST = COLORS.FOREST_GENERAL.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const FOREST_DEEP = THEME.pageBg;
const SAND = THEME.altBg;
const AMBER = THEME.accent;
const AMBER_WARM = COLORS.AMBER_WARM.hex;
const AMBER_DEEP = COLORS.AMBER_DEEP.hex;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;
const LIVE_GREEN = THEME.success;
// Foliage/ground green from the hero art — used for the scroll-down hint arrow
// so it reads as part of the scene rather than a loud accent.
const GROUND_GREEN = COLORS.SAGE_DEEP.hex;

/** Tyler's Microsoft Bookings page — already live and wired to his
 * calendar. RM-flavored CTAs across the landing route here directly
 * instead of through the generic /contact form so leads land in
 * Bookings and pick a slot in one click. */
const BOOKINGS_URL = "https://outlook.office.com/book/CoravuIntake@Coravu.com/";

function pxPad(width: number): number {
  return width >= 900 ? 60 : 28;
}

/**
 * Single source of truth for the hero right-rail width. The header buttons
 * (Access Portal + Create Account) and every rail block below (description
 * card, Student Portal CTA, Build Pipeline CTA) all size to this value so
 * their left + right edges line up exactly.
 */
function heroRailWidth(width: number): number {
  if (width >= 1024) return 420;
  return 360;
}

type SectionKey = "welcome" | "process" | "students" | "companies" | "footer";

const NAV_LINK_DIM = "rgba(237,232,223,0.45)";
// Kept as an empty style object so the dozens of existing callers don't
// need to change. The Coravu wheel CursorFollower is mounted at the root
// layout and ships a global stylesheet that pins `cursor: none` on every
// element (with an I-beam restored on text inputs), so per-element cursor
// styling is no longer needed here.
const WEB_POINTER: ViewStyle = {};
const IS_WEB = Platform.OS === "web";
const WEB_INTERACTION_TRANSITION =
  IS_WEB
    ? ({
        transitionProperty: "transform, background-color, border-color, box-shadow, opacity",
        transitionDuration: "150ms",
        transitionTimingFunction: "ease-out",
      } as ViewStyle)
    : {};
/**
 * Full-viewport scroll-snap (web only). The scroll container declares
 * `scroll-snap-type: y mandatory`; each top-level section carries SNAP_PANEL
 * so its top snaps to the top of the viewport. With `scroll-snap-stop: always`
 * a single scroll gesture advances exactly one panel — the page is viewed one
 * full screen at a time instead of as a continuous scroll. Native has no CSS
 * scroll-snap primitive, so it ignores these and scrolls normally.
 */
const SNAP_PANEL: ViewStyle = IS_WEB
  ? ({ scrollSnapAlign: "start", scrollSnapStop: "always" } as unknown as ViewStyle)
  : {};
/**
 * Blank space between full-page snap panels. Each panel carries this as a
 * bottom margin, so scrolling from one section to the next reveals a short
 * band of the page background (FOREST_DEEP) before the next panel snaps into
 * place — the sections read as separate "pages" with breathing room between
 * them rather than butting edge-to-edge.
 */
const PANEL_GAP = 56;

/** The four listed pages for the side dots, in scroll order. The footer is a
 *  fifth snap section but is intentionally NOT listed here. */
const PAGE_DOTS: ReadonlyArray<{ key: SectionKey; title: string }> = [
  { key: "welcome", title: "Welcome" },
  { key: "process", title: "The Process" },
  { key: "students", title: "Students" },
  { key: "companies", title: "Recruiters" },
];

function useMarketingLandingEntrance(active: boolean) {
  const nav = useSharedValue(0);
  const line1 = useSharedValue(0);
  const line2 = useSharedValue(0);
  const line3 = useSharedValue(0);
  const cta = useSharedValue(0);

  useEffect(() => {
    if (!active) return;
    const easing = Easing.out(Easing.cubic);
    nav.value = withTiming(1, { duration: 300, easing });
    line1.value = withDelay(0, withTiming(1, { duration: 600, easing }));
    line2.value = withDelay(50, withTiming(1, { duration: 600, easing }));
    line3.value = withDelay(100, withTiming(1, { duration: 600, easing }));
    /* Headline last line finishes ~700ms; CTAs begin ~200ms after hero motion completes */
    cta.value = withDelay(900, withTiming(1, { duration: 520, easing }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nav/line*/cta are Reanimated shared values (stable)
  }, [active]);

  const navStyle = useAnimatedStyle(() => ({ opacity: nav.value }));
  const line1Style = useAnimatedStyle(() => ({
    opacity: line1.value,
    transform: [{ translateY: (1 - line1.value) * 18 }],
  }));
  const line2Style = useAnimatedStyle(() => ({
    opacity: line2.value,
    transform: [{ translateY: (1 - line2.value) * 18 }],
  }));
  const line3Style = useAnimatedStyle(() => ({
    opacity: line3.value,
    transform: [{ translateY: (1 - line3.value) * 18 }],
  }));
  const ctaStyle = useAnimatedStyle(() => ({
    opacity: cta.value,
    transform: [{ translateY: (1 - cta.value) * 22 }],
  }));

  return { navStyle, line1Style, line2Style, line3Style, ctaStyle };
}

/**
 * Indeterminate loading bar — a slim amber sweep gliding across a faint amber
 * track. Used on the marketing loading screen (the initial background-load gate
 * and the in-scroll loading panel at the top of the art).
 */
function LoadingBar() {
  const p = useSharedValue(0);
  useEffect(() => {
    p.value = withRepeat(
      withTiming(1, { duration: 1150, easing: Easing.inOut(Easing.cubic) }),
      -1,
      false,
    );
  }, [p]);
  const TRACK = 200;
  const FILL = 70;
  const fillStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: -FILL + p.value * (TRACK + FILL) }],
  }));
  return (
    <View
      style={{
        width: TRACK,
        height: 3,
        borderRadius: 999,
        backgroundColor: "rgba(181,135,58,0.18)",
        overflow: "hidden",
      }}
    >
      <Animated.View
        style={[
          { width: FILL, height: 3, borderRadius: 999, backgroundColor: AMBER },
          fillStyle,
        ]}
      />
    </View>
  );
}

export function CoravuMarketingLanding() {
  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { user, role } = useAuth();
  const scrollRef = useRef<Animated.ScrollView>(null);
  const sectionY = useRef<Partial<Record<SectionKey, number>>>({});
  const [navScrolled, setNavScrolled] = useState(false);
  const navScrolledRef = useRef(false);
  // The fixed nav header stays hidden over the hero and only reveals once the
  // user scrolls toward the second section. navVisible gates pointer events so
  // the invisible header can't intercept hero clicks; navRevealStyle ramps the
  // opacity for a smooth arrival.
  const [navVisible, setNavVisible] = useState(false);
  const navVisibleRef = useRef(false);
  // True while you're on/near the village (main page). Gates the hero overlay's
  // pointer events so its faded-out buttons can't catch clicks on the sections.
  const [villageActive, setVillageActive] = useState(false);
  const villageActiveRef = useRef(false);
  // True once the intro (loading page → hero) has finished.
  const [introDone, setIntroDone] = useState(false);
  // Returning visitor? If they've seen the loading→village intro before, skip
  // it and start straight on the village/overlay section. Persisted on web.
  const [seenIntro] = useState(() => {
    if (!IS_WEB) return false;
    try {
      return !!window.localStorage.getItem("coravu_seen_intro");
    } catch {
      return false;
    }
  });
  // Gate the whole page on the hero background being loaded — nothing renders
  // but a clean loading screen (Coravu wordmark + bar) until the art is ready,
  // so the page never flashes in over a blank/half-loaded background. A hidden
  // preloader <Image> (below) drives this via onLoad. On native the art is
  // bundled, so it's ready immediately.
  const [bgLoaded, setBgLoaded] = useState(!IS_WEB);
  useEffect(() => {
    if (bgLoaded) return;
    // Safety net: never trap the user on the loading screen if onLoad is missed.
    const t = setTimeout(() => setBgLoaded(true), 6000);
    return () => clearTimeout(t);
  }, [bgLoaded]);
  // Actual rendered height of the loading+hero illustration region (the art
  // drives it in normal flow). The hero rests one screen above its bottom.
  const [regionH, setRegionH] = useState(0);
  // The loading screen (top of the art) can be SEEN — you can scroll up to it
  // freely — but it's never a resting place: the instant you stop scrolling
  // above the village, you're glided back down to the main page (bottom of the
  // art). `peeking` is true while above the village; we turn scroll-snap off
  // then so mandatory snap doesn't fight the free upward scroll or our return.
  const [peeking, setPeeking] = useState(false);
  // Mirror refs so the stable JS scroll handler reads fresh values without
  // forcing the Reanimated scroll worklet to be re-created (and go stale).
  const introDoneRef = useRef(false);
  const heroRestYRef = useRef(0);
  const winHRef = useRef(0);
  const peekingRef = useRef(false);
  const scrollYJsRef = useRef(0);
  // Which page-dot is active (0=Welcome, 1=The Process, 2=Students, 3=Recruiters).
  const [activeDot, setActiveDot] = useState(0);
  const activeDotRef = useRef(0);
  // Return-to-village animation bookkeeping.
  const returningRef = useRef(false);
  const returnRafRef = useRef(0);
  const returnExpectedRef = useRef(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fontsReady = useMarketingFonts();
  // Hold the hero entrance (headline + CTAs) until the intro has scrolled the
  // page down to the village — the animation should play as you ARRIVE on the
  // main page, not while it's still off-screen during the descent.
  const entrance = useMarketingLandingEntrance(introDone);

  // Hero headline is a header bar (same dark-forest look as the sticky nav). It
  // drops in FROM THE TOP once you arrive on the main page; its scroll-distance
  // fade is wired up below (heroHeaderStyle), once heroRestY is known.
  const headerDrop = useSharedValue(0);
  // Footer reveal (0→1) — driven by wheeling DOWN once you're at the bottom of
  // the client section. The footer overlays the client in place (no scroll to a
  // different page); the client just dims behind it. footerHSV is the measured
  // footer height (for the slide distance). atBottomRef tracks whether the
  // scroll is at the very end (the client section).
  const footerReveal = useSharedValue(0);
  const footerRevealJS = useRef(0);
  const footerHSV = useSharedValue(600);
  const atBottomRef = useRef(false);
  useEffect(() => {
    if (introDone) {
      headerDrop.value = withTiming(1, {
        duration: 620,
        easing: Easing.out(Easing.cubic),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- headerDrop is a stable shared value
  }, [introDone]);

  /**
   * Per-box ascend-and-fade on scroll.
   *
   * Each right-rail box (2 description cards + 2 CTAs) fades in opacity
   * AND translates upward as its top edge approaches the fixed nav.
   * The upward translate is the key visual: the box appears to lift off
   * the page and rise into the nav as it dissolves — much more
   * intentional and dynamic than just fading in place, and it gives the
   * user a clear motion cue that the content is on its way out.
   *
   * The nav buttons (Access Portal / Create Account) never fade or
   * move — they're the only persistent nav action across all scroll
   * depths.
   */
  const scrollY = useSharedValue(0);
  const railCard1Ref = useRef<View | null>(null);
  const railCard2Ref = useRef<View | null>(null);
  const railCta1Ref = useRef<View | null>(null);
  const railCta2Ref = useRef<View | null>(null);
  const railCard1Y = useSharedValue<number>(99999);
  const railCard2Y = useSharedValue<number>(99999);
  const railCta1Y = useSharedValue<number>(99999);
  const railCta2Y = useSharedValue<number>(99999);

  const NAV_BOTTOM = 72 + insets.top;
  // Fade window is tight and sits right at the nav line: the box stays at
  // full opacity until its top is ~30px below the nav, then fades + lifts
  // quickly across the next 50px of scroll, finishing just as the top
  // crosses behind the nav. Effect should be barely perceptible —
  // content seamlessly disappears as it reaches the nav, not before.
  const FADE_END = NAV_BOTTOM - 20; // visibleTop where progress reaches 0
  const FADE_RANGE = 50;
  const ASCEND_DISTANCE = 30; // px the box lifts up over the fade range
  const progressForTop = (topY: number, sy: number) => {
    "worklet";
    const visibleTop = topY - sy;
    if (visibleTop >= FADE_END + FADE_RANGE) return 1;
    if (visibleTop <= FADE_END) return 0;
    return (visibleTop - FADE_END) / FADE_RANGE;
  };
  const ascendFadeStyle = (topY: number, sy: number) => {
    "worklet";
    const progress = progressForTop(topY, sy);
    return {
      opacity: progress,
      transform: [{ translateY: (progress - 1) * ASCEND_DISTANCE }],
    };
  };
  const railCard1FadeStyle = useAnimatedStyle(() =>
    ascendFadeStyle(railCard1Y.value, scrollY.value),
  );
  const railCard2FadeStyle = useAnimatedStyle(() =>
    ascendFadeStyle(railCard2Y.value, scrollY.value),
  );
  const railCta1FadeStyle = useAnimatedStyle(() =>
    ascendFadeStyle(railCta1Y.value, scrollY.value),
  );
  const railCta2FadeStyle = useAnimatedStyle(() =>
    ascendFadeStyle(railCta2Y.value, scrollY.value),
  );

  useEffect(() => {
    if (!fontsReady) return;
    // Wait for entrance animation (cta finishes around 1420ms) so transforms
    // don't poison the measurement.
    const handle = setTimeout(() => {
      const pairs: Array<[
        React.MutableRefObject<View | null>,
        typeof railCard1Y,
      ]> = [
        [railCard1Ref, railCard1Y],
        [railCard2Ref, railCard2Y],
        [railCta1Ref, railCta1Y],
        [railCta2Ref, railCta2Y],
      ];
      pairs.forEach(([ref, sharedY]) => {
        ref.current?.measureInWindow((_x, y) => {
          sharedY.value = y + scrollY.value;
        });
      });
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shared values are stable
  }, [fontsReady, winW, winH]);

  // Inject the live-indicator pulse @keyframes once on mount (web only).
  // Mirrors the badgePulse rule in coravu.com's index.html — a soft ring radiating
  // from the green dot every 2s. RN doesn't have a CSS-equivalent box-shadow keyframe
  // primitive, so on native we leave the dot static.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const id = "coravu-live-pulse-keyframes";
    if (document.getElementById(id)) return;
    const styleEl = document.createElement("style");
    styleEl.id = id;
    styleEl.textContent = `
      @keyframes coravuLivePulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(127, 203, 122, 0.55); }
        50% { box-shadow: 0 0 0 6px rgba(127, 203, 122, 0); }
      }
    `;
    document.head.appendChild(styleEl);
  }, []);

  const pad = pxPad(winW);
  // Combined loading+hero illustration height: the portrait art (1085×1450) at
  // full layout width drives the region height. bgH is the theoretical value
  // (used as a fallback before the region has measured itself); regionH is the
  // real measured height. The hero rests one screen above the region's bottom.
  const bgH = Math.round((winW * 1450) / 1085);
  // Trim a thin band of pale sand off the very bottom of the art so the village
  // rest view isn't fringed with empty beach. Kept small so it only clips the
  // sand edge and never eats into the village green. Tunable.
  const HERO_BOTTOM_CROP = Math.round(winH * 0.04);
  const regionNaturalH = bgH - HERO_BOTTOM_CROP;
  // The hero rests on the (lightly cropped) bottom of the art — the village —
  // and that bottom edge is the first snap point in the page.
  const heroRestY = Math.max(0, (regionH || regionNaturalH) - winH);

  // Keep the mirror refs current so the stable handler below reads fresh values.
  introDoneRef.current = introDone;
  heroRestYRef.current = heroRestY;
  winHRef.current = winH;

  const setPeekingState = useCallback((next: boolean) => {
    if (peekingRef.current === next) return;
    peekingRef.current = next;
    setPeeking(next);
  }, []);

  const cancelReturn = useCallback(() => {
    if (returnRafRef.current) cancelAnimationFrame(returnRafRef.current);
    returnRafRef.current = 0;
    returningRef.current = false;
  }, []);

  // Quick glide back down to the village (the art's bottom) once the user stops
  // scrolling above it. Short and snappy — the loading screen can be seen but
  // not rested on. Interruptible: if the user scrolls again mid-return we hand
  // control back (handleScroll detects the divergence).
  const returnToVillage = useCallback(() => {
    const sv = scrollRef.current;
    const targetY = heroRestYRef.current;
    const fromY = scrollYJsRef.current;
    const dist = targetY - fromY;
    if (!sv || dist <= 0) {
      setPeekingState(false);
      return;
    }
    returningRef.current = true;
    const frac = Math.min(1, dist / Math.max(1, targetY));
    const duration = 460 + frac * 640; // 460–1100ms — gentle slingshot down
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    const t0 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      const y = fromY + dist * easeOut(t);
      returnExpectedRef.current = y;
      sv.scrollTo({ y, animated: false });
      if (t < 1) {
        returnRafRef.current = requestAnimationFrame(step);
      } else {
        returnRafRef.current = 0;
        returningRef.current = false;
        // Back on the village snap point — re-enable mandatory snap.
        setPeekingState(false);
      }
    };
    returnRafRef.current = requestAnimationFrame(step);
  }, [setPeekingState]);

  // Runs (off the worklet) on every scroll frame. Lets the user scroll freely up
  // onto the loading screen, then brings them straight back to the village the
  // moment scrolling settles. Stable identity (reads refs) so the scroll worklet
  // never goes stale.
  const handleScroll = useCallback(
    (y: number) => {
      scrollYJsRef.current = y;
      if (!introDoneRef.current) return;
      const restY = heroRestYRef.current;

      // While a return glide is in flight, ignore our own programmatic scroll
      // events — but if the user scrolls against it, give control back.
      if (returningRef.current) {
        if (Math.abs(y - returnExpectedRef.current) > 30) {
          cancelReturn();
        } else {
          return;
        }
      }

      if (y < restY - 4) {
        // Above the village (loading / upper art). Snap off so they can roam up
        // freely; arm a short settle timer to glide back down once they stop.
        setPeekingState(true);
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(() => {
          settleTimerRef.current = null;
          if (
            !returningRef.current &&
            scrollYJsRef.current < heroRestYRef.current - 4
          ) {
            returnToVillage();
          }
        }, 70);
      } else {
        // At/below the village — normal snap paging resumes.
        if (settleTimerRef.current) {
          clearTimeout(settleTimerRef.current);
          settleTimerRef.current = null;
        }
        setPeekingState(false);
      }
    },
    [cancelReturn, returnToVillage, setPeekingState],
  );

  // Tidy up the timer / rAF on unmount.
  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      if (returnRafRef.current) cancelAnimationFrame(returnRafRef.current);
    },
    [],
  );

  const captureY =
    (key: SectionKey) =>
    (e: LayoutChangeEvent) => {
      sectionY.current[key] = e.nativeEvent.layout.y;
    };

  const syncNavScrolled = useCallback((next: boolean) => {
    if (navScrolledRef.current === next) return;
    navScrolledRef.current = next;
    setNavScrolled(next);
  }, []);

  const syncNavVisible = useCallback((next: boolean) => {
    if (navVisibleRef.current === next) return;
    navVisibleRef.current = next;
    setNavVisible(next);
  }, []);

  const syncVillageActive = useCallback((next: boolean) => {
    if (villageActiveRef.current === next) return;
    villageActiveRef.current = next;
    setVillageActive(next);
  }, []);

  // Section tops (scroll offsets) for the four listed pages. Welcome is the
  // village rest; the rest are captured via onLayout. Footer is unlisted.
  const sectionTops = useCallback((): number[] => {
    const s = sectionY.current;
    return [
      heroRestYRef.current,
      s.process ?? Number.POSITIVE_INFINITY,
      s.students ?? Number.POSITIVE_INFINITY,
      s.companies ?? Number.POSITIVE_INFINITY,
    ];
  }, []);

  // Which listed page the viewport centre is on → drives the side dots.
  const syncActiveDot = useCallback(
    (y: number) => {
      const tops = sectionTops();
      const probe = y + winHRef.current * 0.5;
      let active = 0;
      for (let i = 0; i < tops.length; i++) if (probe >= tops[i]) active = i;
      if (activeDotRef.current !== active) {
        activeDotRef.current = active;
        setActiveDot(active);
      }
    },
    [sectionTops],
  );

  const setAtBottom = useCallback((next: boolean) => {
    atBottomRef.current = next;
  }, []);

  const scrollToSection = useCallback(
    (i: number) => {
      const y = sectionTops()[i];
      if (Number.isFinite(y)) {
        scrollRef.current?.scrollTo({ y, animated: true });
      }
    },
    [sectionTops],
  );

  const onAnimatedScroll = useAnimatedScrollHandler(
    {
      onScroll: (event) => {
        const y = event.contentOffset.y;
        scrollY.value = y;
        runOnJS(syncNavScrolled)(y > 60);
        // The hero rests at heroRestY (village); the next section (For Students)
        // is one screen below it. Reveal the nav as that section approaches.
        runOnJS(syncNavVisible)(y > heroRestY + winH * 0.6);
        // The hero overlay is interactive only while you're on/near the village.
        runOnJS(syncVillageActive)(Math.abs(y - heroRestY) < winH * 0.5);
        // Track which page the side dots highlight.
        runOnJS(syncActiveDot)(y);
        // At the very bottom (the client section)? → enables the footer reveal.
        runOnJS(setAtBottom)(
          y >= event.contentSize.height - event.layoutMeasurement.height - 4,
        );
        // Off-thread: let the user roam up onto loading, then glide them back
        // down to the village the moment they stop.
        runOnJS(handleScroll)(y);
      },
    },
    [winH, heroRestY, handleScroll, syncActiveDot, setAtBottom],
  );

  // Nav header opacity: 0 over the loading page AND the hero, ramping to 1 as
  // the section after the hero approaches (~2 screens down, since the loading
  // page is panel 0). Hides again if you scroll back up to the hero/loading.
  const navRevealStyle = useAnimatedStyle(() => {
    const start = heroRestY + winH * 0.45;
    const end = heroRestY + winH * 0.8;
    const o =
      scrollY.value >= end
        ? 1
        : scrollY.value <= start
          ? 0
          : (scrollY.value - start) / (end - start);
    return { opacity: o };
  });

  // Page dots: appear once you arrive at the first section (the village) and
  // stay visible through the rest of the pages; hidden over the loading screen.
  const dotsStyle = useAnimatedStyle(() => {
    const start = heroRestY - winH * 0.5;
    const end = heroRestY - winH * 0.12;
    const o =
      scrollY.value >= end
        ? 1
        : scrollY.value <= start
          ? 0
          : (scrollY.value - start) / (end - start);
    return { opacity: o };
  });

  // Footer slides up by its own height as reveal goes 0→1; the scrim dims the
  // client behind it.
  const footerRevealStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: (1 - footerReveal.value) * footerHSV.value }],
  }));
  const footerScrimStyle = useAnimatedStyle(() => ({
    opacity: footerReveal.value * 0.82,
  }));

  const setFooterReveal = useCallback((v: number) => {
    const c = Math.min(1, Math.max(0, v));
    footerRevealJS.current = c;
    footerReveal.value = c;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shared value is stable
  }, []);

  // Wheel-driven footer reveal (web). At the bottom of the client section, a
  // downward wheel raises the footer over the (dimmed) client instead of doing
  // nothing; an upward wheel lowers it back before the page scrolls up again.
  useEffect(() => {
    if (!IS_WEB || typeof window === "undefined") return;
    const onWheel = (e: WheelEvent) => {
      const reveal = footerRevealJS.current;
      const step = e.deltaY / (winHRef.current * 0.9 || 700);
      if (e.deltaY > 0 && (atBottomRef.current || reveal > 0) && reveal < 1) {
        e.preventDefault();
        setFooterReveal(reveal + step);
      } else if (e.deltaY < 0 && reveal > 0) {
        e.preventDefault();
        setFooterReveal(reveal + step);
      }
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [setFooterReveal]);

  // Headline header: pinned at the top of the screen (fixed overlay), fully
  // visible on the main page and fading out as you scroll AWAY in EITHER
  // direction. Combined with the one-time drop-in entrance.
  const heroHeaderStyle = useAnimatedStyle(() => {
    const dist = Math.abs(scrollY.value - heroRestY);
    const scrollFade = 1 - Math.min(1, dist / (winH * 0.4));
    return {
      opacity: headerDrop.value * scrollFade,
      transform: [{ translateY: (headerDrop.value - 1) * 64 }],
    };
  });


  // Scroll-down hint — a gold arrow at the bottom-left. It fades in only after
  // the user has been on the page a moment (~3s), nudging them to keep going,
  // then fades back out as soon as they start scrolling down off the hero.
  const hintReady = useSharedValue(0);
  useEffect(() => {
    if (!bgLoaded) return;
    const t = setTimeout(() => {
      hintReady.value = withTiming(1, { duration: 650, easing: Easing.out(Easing.cubic) });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hintReady is a stable shared value
  }, [bgLoaded]);
  const hintBounce = useSharedValue(0);
  useEffect(() => {
    hintBounce.value = withRepeat(
      withTiming(1, { duration: 1100, easing: Easing.inOut(Easing.quad) }),
      -1,
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hintBounce is a stable shared value
  }, []);
  const hintStyle = useAnimatedStyle(() => {
    // Fade out across the first ~third of a screen of downward scroll off the hero.
    const past = (scrollY.value - heroRestY) / (winH * 0.3);
    const scrollFade = 1 - Math.min(1, Math.max(0, past));
    return { opacity: hintReady.value * scrollFade };
  });
  const hintBounceStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: hintBounce.value * 8 }],
  }));

  // Intro: the green loading page is panel 0 of the scroll. We start at the top
  // (loading) and, once the page is ready, SLOWLY scroll DOWN the power-line
  // path to the village (hero). A custom rAF ease-in-out gives a deliberate,
  // calm descent. Snap is off until this finishes; afterward the loading page
  // is locked out — scrolling up snaps straight back to the village.
  useEffect(() => {
    if (!fontsReady || !regionH) return;
    // Returning visitor — jump straight to the village/overlay (no loading
    // dwell, no slow descent). scroll-behavior is forced to "auto" while
    // booting (below) so this lands instantly.
    if (seenIntro) {
      scrollRef.current?.scrollTo({ y: heroRestY, animated: false });
      setIntroDone(true);
      return;
    }
    // First visit — remember it so next time we skip the intro.
    if (IS_WEB) {
      try {
        window.localStorage.setItem("coravu_seen_intro", "1");
      } catch {
        /* ignore (private mode / unavailable) */
      }
    }
    let raf = 0;
    const hold = setTimeout(() => {
      const targetY = heroRestY;
      const duration = 2400; // slow, deliberate descent
      const easeInOut = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const t0 =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / duration);
        scrollRef.current?.scrollTo({ y: targetY * easeInOut(t), animated: false });
        if (t < 1) {
          raf = requestAnimationFrame(step);
        } else {
          setIntroDone(true);
        }
      };
      raf = requestAnimationFrame(step);
    }, 650);
    return () => {
      clearTimeout(hold);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollRef is stable
  }, [fontsReady, heroRestY, regionH, seenIntro]);

  // Hero CTA stack. Defined here so the same instance can be placed either in
  // the wide-screen right column or, on narrow viewports, inside the left
  // column's subtitle group — without duplicating the two ~60-line Pressables.
  // Web-only drop shadow so the pills lift off the busy village art and stay legible.
  const CTA_SHADOW =
    Platform.OS === "web"
      ? ({ boxShadow: "0 10px 28px rgba(0,0,0,0.45)" } as unknown as ViewStyle)
      : null;
  // Slow, soft gold→linen hover transition (web). A warm off-white (LINEN)
  // reads far more aesthetic than a stark pure white, and a long ease-out lets
  // it glide in. Shared by the pill fill/border and its text.
  const CTA_HOVER_WHITE = COLORS.LINEN.hex;
  const CTA_HOVER_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
  const CTA_HOVER_DURATION = "520ms";
  const CTA_HOVER_TRANSITION = IS_WEB
    ? ({
        transitionProperty: "background-color, border-color, transform, box-shadow",
        // Colour eases slowly; the lift is a touch quicker so it feels responsive.
        transitionDuration: `${CTA_HOVER_DURATION}, ${CTA_HOVER_DURATION}, 320ms, 320ms`,
        transitionTimingFunction: CTA_HOVER_EASE,
      } as unknown as ViewStyle)
    : null;
  // Gentle lift + softer, larger shadow on hover — adds dimension without a jolt.
  const CTA_HOVER_LIFT = IS_WEB
    ? ({
        transform: [{ translateY: -3 }],
        boxShadow: "0 18px 42px rgba(0,0,0,0.5)",
      } as unknown as ViewStyle)
    : null;
  const CTA_TEXT_TRANSITION = IS_WEB
    ? ({
        transitionProperty: "color",
        transitionDuration: CTA_HOVER_DURATION,
        transitionTimingFunction: CTA_HOVER_EASE,
      } as unknown as TextStyle)
    : null;
  const ctaStack = (
    <Animated.View
      style={[{ alignItems: "stretch", width: "100%" }, entrance.ctaStyle]}
    >
      {/* Full-width pills — both stretch so their right-edge arrows line up,
          with an even gap between them. */}
      <View style={{ alignSelf: "stretch", flexDirection: "column", gap: 20 }}>
        {/* Enter Student Portal — translucent green pill, gold outline + text. */}
        <View style={{ alignSelf: "stretch" }}>
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              if (user && role) {
                router.push(roleToRoute(role) as Href);
              } else {
                router.push("/login" as Href);
              }
            }}
            style={({ hovered, pressed }) => [
              {
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                backgroundColor: "rgba(12,18,12,0.72)",
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.55)",
                borderRadius: 999,
                paddingVertical: 15,
                paddingHorizontal: 28,
              },
              CTA_SHADOW,
              CTA_HOVER_TRANSITION,
              WEB_POINTER,
              // Green stays; gold outline + text fade to off-white on hover.
              Platform.OS === "web" && hovered
                ? { borderColor: CTA_HOVER_WHITE }
                : null,
              Platform.OS === "web" && hovered ? CTA_HOVER_LIFT : null,
              pressed ? { opacity: 0.9 } : null,
            ]}
          >
            {({ hovered }) => {
              const c =
                Platform.OS === "web" && hovered ? CTA_HOVER_WHITE : AMBER;
              return (
                <>
                  <Text
                    style={[
                      {
                        fontFamily: FONTS.bodySemiBold,
                        fontSize: 13,
                        letterSpacing: 1.7,
                        textTransform: "uppercase",
                        color: c,
                      },
                      CTA_TEXT_TRANSITION,
                    ]}
                  >
                    Enter The Student Portal
                  </Text>
                  <Text style={[{ fontSize: 16, color: c }, CTA_TEXT_TRANSITION]}>
                    →
                  </Text>
                </>
              );
            }}
          </Pressable>
        </View>

        {/* Build your Recruiting Pipeline — solid deep-forest pill with gold
            outline so it reads clearly against the art. */}
        <View style={{ alignSelf: "stretch" }}>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push("/signup?role=client" as Href)}
            style={({ hovered, pressed }) => [
              {
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                backgroundColor: "rgba(12,18,12,0.72)",
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.55)",
                borderRadius: 999,
                paddingVertical: 15,
                paddingHorizontal: 28,
              },
              CTA_SHADOW,
              CTA_HOVER_TRANSITION,
              WEB_POINTER,
              // Green stays; only the gold outline fades to off-white on hover
              // (the text fades too, in the children below).
              Platform.OS === "web" && hovered
                ? { borderColor: CTA_HOVER_WHITE }
                : null,
              Platform.OS === "web" && hovered ? CTA_HOVER_LIFT : null,
              pressed ? { opacity: 0.88 } : null,
            ]}
          >
            {({ hovered }) => {
              const c =
                Platform.OS === "web" && hovered ? CTA_HOVER_WHITE : AMBER;
              return (
                <>
                  <Text
                    style={[
                      {
                        fontFamily: FONTS.bodySemiBold,
                        fontSize: 13,
                        letterSpacing: 1.7,
                        textTransform: "uppercase",
                        color: c,
                      },
                      CTA_TEXT_TRANSITION,
                    ]}
                  >
                    Build your Recruiting Pipeline
                  </Text>
                  <Text style={[{ fontSize: 16, color: c }, CTA_TEXT_TRANSITION]}>
                    →
                  </Text>
                </>
              );
            }}
          </Pressable>
        </View>
      </View>
    </Animated.View>
  );

  return (
    <View className="flex-1" style={{ backgroundColor: FOREST_DEEP }}>
      <StatusBar barStyle="light-content" />
      {IS_WEB && !bgLoaded ? (
        // Hidden preloader — loads the hero art off-screen so we can gate the
        // page on it without mounting the real (visible) background first.
        <Image
          source={HERO_BG}
          accessibilityIgnoresInvertColors
          onLoad={() => setBgLoaded(true)}
          onError={() => setBgLoaded(true)}
          style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
        />
      ) : null}
      {!bgLoaded ? (
        // Initial loading screen — render nothing else on the page until the
        // hero art is loaded. Just the Coravu wordmark + a loading bar on the
        // brand green, so the page never flashes in over a blank background.
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <View style={{ alignItems: "center", gap: 22 }}>
            <LogoWordmark
              fontSize={26}
              letterSpacing={9}
              color={LINEN}
              accent={AMBER}
            />
            <LoadingBar />
          </View>
        </View>
      ) : (
       <>
      {/* Fixed nav — hidden over the hero, revealed from the second section on. */}
      <Animated.View
        pointerEvents={navVisible ? "auto" : "none"}
        style={[
          {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            paddingTop: insets.top,
            paddingHorizontal: pad,
            height: 72 + insets.top,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottomWidth: navScrolled ? 1 : 0,
            borderBottomColor: "rgba(181,135,58,0.1)",
            backgroundColor: navScrolled ? "rgba(19,30,18,0.94)" : "transparent",
          },
          navRevealStyle,
        ]}
      >
        <Pressable
          accessibilityRole="button"
          className="flex-row items-center"
          style={({ hovered }) => [{ gap: 12 }, WEB_POINTER, hovered && Platform.OS === "web" ? { opacity: 0.9 } : null]}
          onPress={() => scrollRef.current?.scrollTo({ y: heroRestY, animated: true })}
        >
          <LogoMark size={34} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} rays="full" />
          <LogoWordmark fontSize={21.6} letterSpacing={7.5} color={LINEN} accent={AMBER} />
        </Pressable>
        {winW >= 900 ? (
          // Width matches the hero right rail so all four blocks (this button
          // pair, the description card, and both CTAs below) share the same
          // left + right edges. flex:1 on each button keeps them equal width
          // regardless of label length. These buttons never fade — they're
          // the only persistent nav action available across all scroll
          // positions.
          <View style={{ width: heroRailWidth(winW), flexDirection: "row", gap: 12 }}>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push("/login" as Href)}
              style={({ hovered, pressed }) => [
                {
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.55)",
                  borderRadius: 8,
                  paddingVertical: 9,
                  paddingHorizontal: 12,
                },
                WEB_INTERACTION_TRANSITION,
                WEB_POINTER,
                Platform.OS === "web" && hovered
                  ? {
                      backgroundColor: "rgba(181,135,58,0.12)",
                      borderColor: AMBER,
                    }
                  : null,
                pressed ? { opacity: 0.88 } : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodySemiBold,
                  fontSize: 11.7,
                  letterSpacing: 1.7,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Access Portal
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push("/signup" as Href)}
              style={({ hovered, pressed }) => [
                {
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: AMBER,
                  borderWidth: 1,
                  borderColor: AMBER,
                  borderRadius: 8,
                  paddingVertical: 9,
                  paddingHorizontal: 12,
                },
                WEB_INTERACTION_TRANSITION,
                WEB_POINTER,
                Platform.OS === "web" && hovered
                  ? {
                      backgroundColor: AMBER_DEEP,
                      borderColor: AMBER_DEEP,
                    }
                  : null,
                pressed ? { opacity: 0.9 } : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodySemiBold,
                  fontSize: 11.7,
                  letterSpacing: 1.7,
                  textTransform: "uppercase",
                  color: THEME.accentText,
                }}
              >
                Create Account
              </Text>
            </Pressable>
          </View>
        ) : (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ alignItems: "center", gap: 20, paddingLeft: 8, paddingRight: 8 }}>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push("/login" as Href)}
              style={({ hovered, pressed }) => [
                { borderWidth: 1, borderColor: "rgba(181,135,58,0.55)", borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14 },
                WEB_INTERACTION_TRANSITION,
                WEB_POINTER,
                Platform.OS === "web" && hovered
                  ? { backgroundColor: "rgba(181,135,58,0.12)", borderColor: AMBER, transform: [{ scale: 1.02 }] }
                  : null,
                pressed ? { opacity: 0.88 } : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
                Access Portal
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push("/signup" as Href)}
              style={({ hovered, pressed }) => [
                { backgroundColor: AMBER, borderWidth: 1, borderColor: AMBER, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14 },
                WEB_INTERACTION_TRANSITION,
                WEB_POINTER,
                Platform.OS === "web" && hovered
                  ? {
                      backgroundColor: AMBER_DEEP,
                      borderColor: AMBER_DEEP,
                      transform: [{ scale: 1.02 }],
                    }
                  : null,
                pressed ? { opacity: 0.9 } : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.accentText }}>
                Create Account
              </Text>
            </Pressable>
          </ScrollView>
        )}
      </Animated.View>

      <Animated.ScrollView
        ref={scrollRef}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 12 }}
        scrollEventThrottle={16}
        onScroll={onAnimatedScroll}
        showsVerticalScrollIndicator={false}
        style={
          Platform.OS === "web"
            ? ({
                // While peeking (above the village) use "auto" so our per-frame
                // return scroll lands exactly where we put it. With "smooth" the
                // browser eases programmatic scrolls, so the reported position
                // lags our target — which would trip the return's "user moved"
                // check and strand you on the loading screen. Normal paging
                // keeps "smooth".
                // Also "auto" during a returning-visitor boot so the jump to the
                // village lands instantly instead of smooth-scrolling.
                scrollBehavior:
                  peeking || (seenIntro && !introDone) ? "auto" : "smooth",
                // Snap to pages — the village (hero) and the sections below are
                // snap targets. The loading page is deliberately NOT a snap
                // target so it can't be a resting place; snap is also off while
                // `peeking` (scrolled up above the village) so the user can roam
                // up freely and our return glide isn't fought. Off entirely
                // during the intro auto-scroll too.
                scrollSnapType: introDone && !peeking ? "y mandatory" : "none",
              } as unknown as ViewStyle)
            : undefined
        }
      >
        {/* ── LOADING + HERO — the tall full-width photo as an absolute
            background, with two real flow snap pages over it: the loading page
            (top, beavers) and the hero page (bottom, village). A spacer between
            them holds the photo's middle (the power-line path). */}
        <View
          onLayout={(e) => setRegionH(e.nativeEvent.layout.height)}
          style={{ width: "100%", position: "relative" }}
        >
          {/* PHOTO — absolute full-bleed background. overflow:hidden lives HERE
              (not on the region) so the bottom HERO_BOTTOM_CROP of sand is
              clipped WITHOUT turning the region into a scroll container — that
              would steal the hero page's scroll-snap-align from the main
              ScrollView and drop the village as a snap point. */}
          <View
            className="absolute inset-0"
            pointerEvents="none"
            style={{ overflow: "hidden" }}
          >
            <Image
              source={HERO_BG}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
              style={{ position: "absolute", top: 0, left: 0, right: 0, width: "100%", height: bgH }}
            />
            <Fireflies viewportHeight={winH} />
          </View>

          {/* LOADING page — flow, one screen. Deliberately NOT a snap target:
              you can scroll up to see it, but the moment you stop you're glided
              back down to the village (see handleScroll / returnToVillage).
              Coravu wordmark + loading bar (no logo mark). */}
          <View
            pointerEvents="box-none"
            style={{
              height: winH,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 6,
            }}
          >
            <View style={{ alignItems: "center", gap: 22 }}>
              <LogoWordmark
                fontSize={26}
                letterSpacing={9}
                color={LINEN}
                accent={AMBER}
              />
              {!introDone ? <LoadingBar /> : null}
            </View>
          </View>

          {/* MIDDLE — the photo's power-line path between the two pages. Shorter
              by HERO_BOTTOM_CROP so the hero page lifts off the sand edge. */}
          <View
            pointerEvents="none"
            style={{ height: Math.max(0, bgH - 2 * winH - HERO_BOTTOM_CROP) }}
          />

          {/* HERO page — flow, one screen, snap target (the village). The
              headline + CTAs live in the fixed frosted overlay (heroOverlay
              below), not in this scroll flow. */}
          <View
            pointerEvents="none"
            style={[{ height: winH, zIndex: 10 }, SNAP_PANEL]}
          />
        </View>

        {/* ── THE CORAVU PROCESS — on SAND. Four phases laid out side-by-side
            on desktop, 2×2 on tablet, stacked on mobile. */}
        <View
          onLayout={captureY("process")}
          style={[
            {
              minHeight: winH,
              backgroundColor: SAND,
              paddingHorizontal: pad,
              paddingVertical: winW >= 900 ? 72 : 48,
              justifyContent: "center",
              marginBottom: PANEL_GAP,
              position: "relative",
            },
            SNAP_PANEL,
          ]}
        >
          <Reveal>
            <View className="mb-5 flex-row items-center" style={{ gap: 12 }}>
              <View style={{ width: 28, height: 1, backgroundColor: AMBER }} />
              <Text
                style={{
                  fontFamily: FONTS.bodySemiBold,
                  fontSize: 10.5,
                  letterSpacing: 3.5,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                The Coravu Process
              </Text>
            </View>
          </Reveal>
          <Reveal>
            <Text
              style={{
                fontFamily: FONTS.displayLight,
                fontSize: winW >= 900 ? 58 : 38,
                lineHeight: winW >= 900 ? 60 : 42,
                color: FOREST,
                marginBottom: 16,
              }}
            >
              From idea{"\n"}
              <Text style={{ fontFamily: FONTS.displayItalic, color: AMBER }}>
                to hired talent.
              </Text>
            </Text>
          </Reveal>
          <Reveal>
            <Text
              style={{
                fontFamily: FONTS.bodyLight,
                fontSize: 16,
                lineHeight: 27.2,
                color: "rgba(28,43,26,0.6)",
                maxWidth: 620,
                marginBottom: 40,
              }}
            >
              Four phases, transparent at every step — built for companies finding
              talent and students proving they belong in the room.
            </Text>
          </Reveal>

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: winW >= 900 ? 32 : 24,
            }}
          >
            {[
              {
                n: "01",
                title: "The Brief",
                desc: "A company partners with Coravu to design a real business challenge — a case built around the talent they actually need to hire.",
              },
              {
                n: "02",
                title: "The Competition",
                desc: "Students compete in structured rounds, solo or in teams. Real prize pools. Real deliverables. Real deadlines.",
              },
              {
                n: "03",
                title: "Live Performance",
                desc: "Companies watch the work unfold in real time through a private dashboard — every submission, every team, every candidate visible at a glance.",
              },
              {
                n: "04",
                title: "The Hire",
                desc: "Top performers move into fast-track interviews. Companies recruit on proof of work, not paper credentials.",
              },
            ].map((step) => {
              // Responsive column count: 4 ≥1024, 2 ≥768, 1 below.
              // gap is 32 on wide / 24 on narrow; subtract proportionally
              // from the column percentage so items fit without overflowing.
              const cols = winW >= 1024 ? 4 : winW >= 768 ? 2 : 1;
              const gapPx = winW >= 900 ? 32 : 24;
              const totalGap = gapPx * (cols - 1);
              const basis =
                cols === 1
                  ? "100%"
                  : `calc(${100 / cols}% - ${totalGap / cols}px)`;
              return (
                <Reveal key={step.n} style={{ flexBasis: basis as unknown as number, flexGrow: 0, flexShrink: 0 } as ViewStyle}>
                  {/* Pressable wrapper carries the hover effect — AMBER glow
                      + 2px lift. accessibilityRole="text" because the steps
                      aren't navigable; the hover is decorative polish only. */}
                  <Pressable accessibilityRole="text" onPress={undefined}>
                    {({ hovered }) => {
                      const isHover = Platform.OS === "web" && Boolean(hovered);
                      return (
                        <View
                          style={[
                            {
                              padding: 24,
                              borderRadius: 8,
                              borderWidth: 1,
                              borderColor: isHover
                                ? "rgba(181,135,58,0.4)"
                                : "rgba(181,135,58,0)",
                              backgroundColor: isHover
                                ? "rgba(181,135,58,0.04)"
                                : "transparent",
                              transform: isHover ? [{ translateY: -2 }] : undefined,
                            } as ViewStyle,
                            Platform.OS === "web"
                              ? ({
                                  transitionProperty:
                                    "transform, box-shadow, border-color, background-color",
                                  transitionDuration: "220ms",
                                  transitionTimingFunction: "ease-out",
                                } as ViewStyle)
                              : null,
                            isHover
                              ? ({
                                  boxShadow:
                                    "0 0 0 1px rgba(181,135,58,0.4), 0 0 32px rgba(181,135,58,0.15)",
                                } as ViewStyle)
                              : null,
                          ]}
                        >
                          <Text
                            style={{
                              fontFamily: FONTS.displayLight,
                              fontSize: 64,
                              lineHeight: 64,
                              color: AMBER,
                            }}
                          >
                            {step.n}
                          </Text>
                          <View
                            style={{
                              width: 40,
                              height: 1,
                              backgroundColor: AMBER,
                              marginTop: 12,
                              marginBottom: 24,
                            }}
                          />
                          <Text
                            style={{
                              fontFamily: FONTS.displayMedium,
                              fontSize: 22,
                              lineHeight: 28,
                              color: FOREST,
                              marginBottom: 12,
                            }}
                          >
                            {step.title}
                          </Text>
                          <Text
                            style={{
                              fontFamily: FONTS.body,
                              fontSize: 14.5,
                              lineHeight: 23.9,
                              color: "rgba(28,43,26,0.6)",
                            }}
                          >
                            {step.desc}
                          </Text>
                        </View>
                      );
                    }}
                  </Pressable>
                </Reveal>
              );
            })}
          </View>
        </View>

        {/* ── FOR STUDENTS — on FOREST_DEEP (green). Text is light (LINEN); the
            LockedCompetitionCard and "Start competing today" callout are dark
            embedded panels that read on the green section bg. */}
        <View
          onLayout={captureY("students")}
          style={[
            {
              backgroundColor: FOREST_DEEP,
              paddingHorizontal: pad,
              paddingTop: winW >= 900 ? 72 : 56,
              paddingBottom: 140,
              minHeight: winH,
              marginBottom: PANEL_GAP,
              position: "relative",
            },
            SNAP_PANEL,
          ]}
        >
          <Svg
            width={820}
            height={820}
            viewBox="0 0 820 820"
            style={{ position: "absolute", right: -80, top: -60, opacity: 0.06 }}
          >
            <G stroke={SAGE} strokeLinecap="round" fill="none">
              <Path
                d="M410 800 C396 650 368 500 335 390 C309 295 276 215 252 144 C232 82 224 32 220 -4"
                strokeWidth={4}
              />
              <Path
                d="M335 390 C418 352 512 326 600 292 C672 263 734 228 778 196 C814 170 830 144 832 122"
                strokeWidth={3}
              />
              <Path d="M344 434 C428 398 524 374 614 342 C688 315 752 282 796 251" strokeWidth={2.4} />
              <Path d="M335 390 C252 352 160 326 74 292 C4 263 -56 228 -98 196" strokeWidth={3} />
              <Path d="M490 326 C484 298 472 274 455 248" strokeWidth={1.6} />
              <Path d="M562 296 C556 268 544 244 527 218" strokeWidth={1.6} />
              <Path d="M165 326 C171 298 183 274 200 248" strokeWidth={1.6} />
              <Path d="M420 348 C414 320 402 296 386 270" strokeWidth={1.6} />
            </G>
          </Svg>
          <Svg
            width={600}
            height={600}
            viewBox="0 0 600 600"
            style={{
              position: "absolute",
              left: -100,
              bottom: -80,
              opacity: 0.05,
              transform: [{ rotate: "180deg" }, { scaleX: -1 }],
            }}
          >
            <G stroke={SAGE} strokeLinecap="round" fill="none">
              <Path d="M300 580 C290 470 268 360 244 272 C224 198 200 132 182 76 C168 30 162 -4 160 -28" strokeWidth={3} />
              <Path d="M244 272 C302 246 370 226 432 200 C484 178 526 152 554 128" strokeWidth={2.2} />
              <Path d="M244 272 C186 246 120 226 60 200 C10 178 -30 152 -56 128" strokeWidth={2.2} />
              <Path d="M355 226 C350 204 340 185 326 163" strokeWidth={1.2} />
              <Path d="M410 202 C405 180 395 161 381 139" strokeWidth={1.2} />
            </G>
          </Svg>

          <View className={winW >= 900 ? "flex-row" : "flex-col"} style={{ gap: winW >= 900 ? 100 : 52, zIndex: 2 }}>
            <Reveal style={{ flex: 1 }}>
              <View className="flex-1">
                <View className="mb-3 flex-row items-center" style={{ gap: 12 }}>
                  <View style={{ width: 28, height: 1, backgroundColor: AMBER }} />
                  <Text
                    style={{
                      fontFamily: FONTS.bodySemiBold,
                      fontSize: 10.9,
                      letterSpacing: 3.5,
                      textTransform: "uppercase",
                      color: AMBER,
                    }}
                  >
                    For Students
                  </Text>
                </View>
                <View style={{ alignItems: "center", marginTop: 56 }}>
                  <Text
                    style={{
                      fontFamily: FONTS.display,
                      fontSize: winW >= 900 ? 74 : 44,
                      lineHeight: winW >= 900 ? 84 : 52,
                      color: LINEN,
                      marginBottom: 48,
                      textAlign: "center",
                    }}
                  >
                    <Text style={{ color: AMBER }}>Win,</Text>{" "}
                    <Text style={{ fontFamily: FONTS.displayItalic }}>don&apos;t apply.</Text>
                  </Text>
                  <View style={{ alignItems: "flex-start" }}>
                    {[
                      "Solve real company challenges",
                      "Team up or compete solo",
                      "Top ranks earn fast-track interviews",
                    ].map((t) => (
                      <View
                        key={t}
                        className="flex-row"
                        style={{ gap: 16, marginBottom: 40, alignItems: "center" }}
                      >
                        <View
                          className="items-center justify-center rounded-full"
                          style={{
                            width: 22,
                            height: 22,
                            borderWidth: 1,
                            borderColor: AMBER,
                          }}
                        >
                          <View className="rounded-full" style={{ width: 8, height: 8, backgroundColor: AMBER }} />
                        </View>
                        <Text
                          style={{
                            fontFamily: FONTS.displayMedium,
                            fontSize: 22,
                            lineHeight: 30,
                            color: LINEN,
                          }}
                        >
                          {t}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            </Reveal>

            <Reveal style={{ flex: 1 }}>
              <View className="flex-1" style={{ gap: 12 }}>
              {[
                {
                  tag: "Strategy · Case Competition",
                  status: "active" as const,
                  title: "Market Entry Case Challenge",
                  meta: ["Teams of 2–4", "12 days remaining", "Prize pool available"],
                },
                {
                  tag: "Finance · Case Competition",
                  status: "soon" as const,
                  title: "Growth Strategy Analysis",
                  meta: ["Individual", "Opens in 5 days", "Fast-track interview prize"],
                },
              ].map((card) => (
                <LockedCompetitionCard key={card.title} {...card} />
              ))}
              <View
                style={{
                  marginTop: 12,
                  paddingVertical: 32,
                  paddingHorizontal: 36,
                  backgroundColor: FOREST_MID,
                  borderLeftWidth: 3,
                  borderLeftColor: AMBER,
                  borderRadius: 4,
                  alignSelf: "stretch",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 9.6,
                    letterSpacing: 3.2,
                    textTransform: "uppercase",
                    color: AMBER,
                    marginBottom: 8,
                  }}
                >
                  Competitions are live
                </Text>
                <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 29.6, color: LINEN, marginBottom: 14 }}>
                  Start competing today
                </Text>
                <Pressable
                  onPress={() => router.push("/signup?role=student" as Href)}
                  style={({ hovered, pressed }) => [
                    WEB_POINTER,
                    Platform.OS === "web" && hovered ? { transform: [{ scale: 1.02 }] } : null,
                    pressed ? { opacity: 0.85 } : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11.5,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      color: AMBER,
                      borderBottomWidth: 1,
                      borderBottomColor: "rgba(181,135,58,0.35)",
                      alignSelf: "flex-start",
                    }}
                  >
                    Create account →
                  </Text>
                </Pressable>
              </View>
              </View>
            </Reveal>
          </View>
        </View>

        {/* ── FOR COMPANIES (Recruiters) — on SAND (cream). Headline + paragraph
            are dark (FOREST); the CompanyOfferingCards become dark embedded
            panels so they read on the light section bg. */}
        <View
          onLayout={captureY("companies")}
          style={[
            {
              backgroundColor: SAND,
              paddingHorizontal: pad,
              paddingTop: winW >= 900 ? 72 : 56,
              paddingBottom: 10,
              minHeight: winH,
              // Last section — no trailing gap, so the scroll bottom lands on
              // its snap point (keeps the footer-reveal trigger stable).
              position: "relative",
            },
            SNAP_PANEL,
          ]}
        >
          <Svg
            width={650}
            height={650}
            viewBox="0 0 650 650"
            style={{ position: "absolute", right: -60, bottom: -80, opacity: 0.05 }}
          >
            <G stroke={FOREST} strokeLinecap="round" fill="none">
              <Path
                d="M325 630 C314 510 290 390 264 295 C243 215 216 145 198 84 C183 32 177 -8 174 -36"
                strokeWidth={3.2}
              />
              <Path d="M264 295 C332 264 410 240 482 210 C542 184 592 153 625 125" strokeWidth={2.4} />
              <Path d="M272 332 C342 303 422 281 496 253 C558 229 610 200 642 174" strokeWidth={1.9} />
              <Path d="M264 295 C196 264 120 240 50 210 C-8 184 -56 153 -88 125" strokeWidth={2.4} />
              <Path d="M385 240 C380 216 370 196 356 174" strokeWidth={1.3} />
              <Path d="M445 215 C440 191 430 171 416 149" strokeWidth={1.3} />
              <Path d="M145 240 C150 216 160 196 174 174" strokeWidth={1.3} />
            </G>
          </Svg>

          <View style={{ zIndex: 2 }}>
            {/* HEADLINE — eyebrow + two-sentence headline, mirroring For Students */}
            <Reveal>
              <View style={{ maxWidth: 760 }}>
                <View className="mb-3 flex-row items-center" style={{ gap: 12 }}>
                  <View style={{ width: 28, height: 1, backgroundColor: AMBER }} />
                  <Text
                    style={{
                      fontFamily: FONTS.bodySemiBold,
                      fontSize: 10.9,
                      letterSpacing: 3.5,
                      textTransform: "uppercase",
                      color: AMBER,
                    }}
                  >
                    For Companies
                  </Text>
                </View>
                <Text style={{ fontFamily: FONTS.display, fontSize: winW >= 900 ? 51 : 34, lineHeight: winW >= 900 ? 56 : 38, color: FOREST, marginBottom: 20 }}>
                  See the talent.{"\n"}
                  <Text style={{ fontFamily: FONTS.displayItalic, color: AMBER }}>Before you hire.</Text>
                </Text>
                <Text
                  style={{
                    fontFamily: FONTS.bodyLight,
                    fontSize: 14.7,
                    lineHeight: 27,
                    color: "rgba(28,43,26,0.6)",
                    marginBottom: 40,
                    maxWidth: 600,
                  }}
                >
                  Pick the engagement for your team.
                </Text>
              </View>
            </Reveal>

            {/* OFFERING CARDS — side-by-side on desktop, stacked under 900px */}
            <View
              className={winW >= 900 ? "flex-row" : "flex-col"}
              style={{ gap: winW >= 900 ? 24 : 20, alignItems: "stretch", marginBottom: 56 }}
            >
              <Reveal style={{ flex: 1 }}>
                <CompanyOfferingCard
                  variant="rm"
                  eyebrow="Coravu Managed"
                  title="Coravu manages the program for you."
                  body="Coravu designs the case brief, recruits and manages the student field, judges the work, and surfaces the top talent for your team to hire. Zero internal lift."
                  bullets={[
                    "Custom case briefs designed by our team",
                    "Vetted candidates with verified work, not résumé claims",
                    "Live access to every team's submission",
                    "Top performers earn fast-track interview slots",
                    "Analytics on team performance and decision quality",
                  ]}
                  ctaLabel="Talk to Coravu →"
                />
              </Reveal>
              <Reveal variant="fade-left" style={{ flex: 1 }}>
                <CompanyOfferingCard
                  variant="licensing"
                  eyebrow="Self-Managed"
                  title="You manage the program."
                  body="Use Coravu's platform to host your own competition. Tap our student pool and case-management tools while keeping full editorial control over the brief, deliverables, and judging. We provide the rails; you drive."
                  bullets={[
                    "Full-access case editor for briefs, deliverables, and rubrics",
                    "Tap Coravu's existing student pool — no recruiting cold-start",
                    "Invite your own judges or use Coravu-vetted ones",
                    "Your branding on the candidate-facing case page",
                    "Self-serve dashboards for candidates, teams, and submissions",
                  ]}
                  ctaLabel="Explore the platform →"
                  ctaHref={"/contact?topic=licensing" as Href}
                />
              </Reveal>
            </View>

            <Reveal variant="fade-left">
              {/* Build Your Pipeline — primary text link (was a filled button;
                  flipped to plain AMBER text on the dark section so it stops
                  competing with the engagement cards' CTAs above). */}
              <Pressable
                onPress={() => router.push("/signup?role=client" as Href)}
                accessibilityRole="link"
                className="mt-6 flex-row items-center self-start"
                style={({ hovered, pressed }) => [
                  { gap: 10 },
                  WEB_POINTER,
                  Platform.OS === "web" && hovered
                    ? { transform: [{ translateX: 4 }] }
                    : null,
                  pressed ? { opacity: 0.7 } : null,
                ]}
              >
                {({ hovered }) => {
                  const isHover = Platform.OS === "web" && hovered;
                  const color = isHover ? AMBER_WARM : AMBER;
                  return (
                    <>
                      <Text
                        style={{
                          fontFamily: FONTS.bodySemiBold,
                          fontSize: 13,
                          letterSpacing: 1.8,
                          textTransform: "uppercase",
                          color,
                        }}
                      >
                        Build Your Pipeline
                      </Text>
                      <Svg width={14} height={14} viewBox="0 0 13 13">
                        <Path
                          d="M2 6.5h9M7.5 3l3.5 3.5L7.5 10"
                          stroke={color}
                          strokeWidth={1.6}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </Svg>
                    </>
                  );
                }}
              </Pressable>

            </Reveal>
          </View>
        </View>


      </Animated.ScrollView>

      {/* FOOTER REVEAL — a scrim that grays the client and a footer panel that
          slides up from the bottom as you scroll down past the client. The
          footer is the "footer version" of the /brand header: dark green, gold
          line, but hung from the BOTTOM (rounded TOP corners, no bottom line). */}
      {bgLoaded ? (
        <>
          <Animated.View
            pointerEvents="none"
            style={[
              {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(8,11,9,1)",
                zIndex: 175,
              },
              footerScrimStyle,
            ]}
          />
          <Animated.View
            pointerEvents="box-none"
            style={[
              { position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 180 },
              footerRevealStyle,
            ]}
          >
            <View
              onLayout={(e) => {
                footerHSV.value = e.nativeEvent.layout.height;
              }}
              style={{
                marginHorizontal: pad,
                backgroundColor: "rgba(26,26,26,1)",
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
                borderTopWidth: 1,
                borderLeftWidth: 1,
                borderRightWidth: 1,
                borderBottomWidth: 0,
                borderColor: "rgba(181,135,58,0.55)",
                overflow: "hidden",
              }}
            >
              <MarketingFooter surface="rgba(26,26,26,1)" />
            </View>
          </Animated.View>
        </>
      ) : null}

      {/* PAGE DOTS — right-side indicator of which listed page you're on. The
          active dot stretches into a taller pill, and the page name shows beside
          it. Appears from the first section (the village) onward. */}
      {bgLoaded ? (
        <Animated.View
          pointerEvents="box-none"
          style={[
            {
              position: "absolute",
              right: pad,
              top: 0,
              bottom: 0,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 16,
              zIndex: 160,
            },
            dotsStyle,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodySemiBold,
              fontSize: 11.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            {PAGE_DOTS[activeDot].title}
          </Text>
          <View style={{ flexDirection: "column", gap: 14, alignItems: "center" }}>
            {PAGE_DOTS.map((d, i) => {
              const active = i === activeDot;
              return (
                <Pressable
                  key={d.key}
                  accessibilityRole="button"
                  onPress={() => scrollToSection(i)}
                  hitSlop={8}
                  style={WEB_POINTER}
                >
                  <View
                    style={[
                      {
                        width: 8,
                        height: active ? 26 : 8,
                        borderRadius: 999,
                        backgroundColor: active ? AMBER : "rgba(247,244,238,0.4)",
                      },
                      IS_WEB
                        ? ({
                            transitionProperty: "height, background-color",
                            transitionDuration: "320ms",
                            transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)",
                          } as unknown as ViewStyle)
                        : null,
                    ]}
                  />
                </Pressable>
              );
            })}
          </View>
        </Animated.View>
      ) : null}

      {/* HERO OVERLAY — just the two CTAs, centered over the village (no card,
          no headline). Stays put while you scroll and fades out as you move away
          from the village in either direction. pointerEvents are gated so the
          (invisible) buttons can't catch clicks on the sections below. */}
      {bgLoaded ? (
        <Animated.View
          pointerEvents={villageActive ? "box-none" : "none"}
          style={[
            {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              justifyContent: "center",
              alignItems: "center",
              paddingHorizontal: pad,
              zIndex: 150,
            },
            heroHeaderStyle,
          ]}
        >
          <View pointerEvents="box-none" style={{ width: "100%", maxWidth: 460 }}>
            {ctaStack}
          </View>
        </Animated.View>
      ) : null}

      {/* Scroll-down hint — gold arrow, bottom-left. Fades in ~3s after landing,
          gently bobs, and fades out as soon as you scroll down off the hero. */}
      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            left: pad,
            bottom: 26 + insets.bottom,
            zIndex: 150,
          },
          hintStyle,
        ]}
      >
        <Animated.View style={hintBounceStyle}>
          <Svg width={34} height={56} viewBox="0 0 34 56">
            <Line
              x1={17}
              y1={0}
              x2={17}
              y2={37}
              stroke={GROUND_GREEN}
              strokeWidth={2.6}
              strokeLinecap="round"
            />
            <Path
              d="M6 34 L17 46 L28 34"
              stroke={GROUND_GREEN}
              strokeWidth={3.2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </Animated.View>
      </Animated.View>
       </>
      )}
    </View>
  );
}

/**
 * One of two offering cards (Coravu RM / Licensing) in the For Companies
 * section. Both share the same vertical structure so they read as a paired
 * comparison; only the surface palette and eyebrow tint change between
 * variants. The RM variant mirrors the gold-card treatment used on the
 * client Dashboard's RM program tile.
 */
function CompanyOfferingCard({
  variant,
  eyebrow,
  title,
  body,
  bullets,
  ctaLabel,
  ctaHref,
}: {
  variant: "rm" | "licensing";
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  ctaLabel: string;
  // Only the licensing variant routes via router.push; the RM variant
  // opens Bookings in a new tab from the press handler below, so its
  // call site doesn't pass an Href at all.
  ctaHref?: Href;
}) {
  const isRM = variant === "rm";
  return (
    <Pressable
      // Outer Pressable is hover-only — no onPress so it doesn't conflict
      // with the inner CTA's button role. Card is dark translucent (LINEN
      // at 4% over the FOREST_DEEP section) so it reads as belonging to
      // the dark section while the AMBER border + glow hover still pop.
      style={({ hovered }) => {
        const isHover = Platform.OS === "web" && hovered;
        return [
          {
            flex: 1,
            padding: 28,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: isHover ? AMBER : "rgba(181,135,58,0.35)",
            backgroundColor: "rgba(19,30,18,0.92)",
            position: "relative",
            // RM keeps a subtle warm overlay so the variants still read as
            // distinct without separate text colors.
            ...(Platform.OS === "web" && isRM
              ? ({
                  backgroundImage:
                    "linear-gradient(135deg, rgba(201,162,74,0.05) 0%, rgba(201,162,74,0.14) 100%)",
                } as object)
              : null),
            ...(Platform.OS === "web"
              ? ({
                  transitionProperty: "transform, box-shadow, border-color",
                  transitionDuration: "180ms",
                  transitionTimingFunction: "ease-out",
                } as ViewStyle)
              : null),
            ...(isHover
              ? ({
                  boxShadow:
                    `0 0 0 1px ${AMBER}, 0 0 24px rgba(181,135,58,0.25)`,
                  transform: [{ scale: 1.01 }],
                } as ViewStyle)
              : null),
          } as ViewStyle,
        ];
      }}
    >
      {/* Coravu wheel mark on the Managed variant only — anchors the card as
          "this is the version where Coravu runs it for you." Self-Managed
          intentionally has no mark. */}
      {isRM ? (
        <View
          accessibilityLabel="Coravu"
          style={{ position: "absolute", top: 20, right: 20 }}
        >
          <Svg width={22} height={22} viewBox="0 0 34 34" fill="none">
            <Circle cx={17} cy={17} r={9.5} stroke={AMBER} strokeWidth={0.8} fill="none" opacity={0.55} />
            <Circle cx={17} cy={17} r={5.5} stroke={AMBER} strokeWidth={1.2} fill="none" />
            <Circle cx={17} cy={17} r={1.8} fill={AMBER} />
            <Line x1={17} y1={5} x2={17} y2={8.5} stroke={AMBER} strokeWidth={0.8} opacity={0.55} />
            <Line x1={17} y1={25.5} x2={17} y2={29} stroke={AMBER} strokeWidth={0.8} opacity={0.55} />
            <Line x1={5} y1={17} x2={8.5} y2={17} stroke={AMBER} strokeWidth={0.8} opacity={0.55} />
            <Line x1={25.5} y1={17} x2={29} y2={17} stroke={AMBER} strokeWidth={0.8} opacity={0.55} />
          </Svg>
        </View>
      ) : null}
      <View className="mb-3 flex-row items-center" style={{ gap: 8, paddingRight: isRM ? 30 : 0 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.4,
            letterSpacing: 3.2,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          {eyebrow}
        </Text>
        {isRM ? (
          <View
            className="rounded-full"
            style={[
              { width: 6, height: 6, backgroundColor: LIVE_GREEN },
              Platform.OS === "web"
                ? ({ animation: "coravuLivePulse 2s ease-in-out infinite" } as unknown as ViewStyle)
                : null,
            ]}
          />
        ) : null}
      </View>
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 28,
          lineHeight: 34,
          color: LINEN,
          marginBottom: 12,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 15,
          lineHeight: 24,
          color: "rgba(247,244,238,0.75)",
          marginBottom: 18,
        }}
      >
        {body}
      </Text>
      {bullets.map((t) => (
        // alignItems: flex-start so the icon stays anchored to the first
        // text line on bullets that wrap to two lines (otherwise the row
        // centers the icon against the visual middle of the wrapped block).
        <View key={t} className="mb-3" style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
          {/* Concentric AMBER ring + dot. Both circles share cx/cy at the
              viewBox center so the dot lands perfectly centered — the
              earlier View-with-borderRadius version was off by ~0.5px due
              to odd/even sub-pixel rounding on the inner dot. */}
          <View style={{ marginTop: 4 }}>
            <Svg width={14} height={14} viewBox="0 0 16 16">
              <Circle cx={8} cy={8} r={6.5} stroke={AMBER} strokeWidth={1} fill="none" />
              <Circle cx={8} cy={8} r={2} fill={AMBER} />
            </Svg>
          </View>
          <Text
            style={{
              flex: 1,
              fontFamily: FONTS.body,
              fontSize: 14.5,
              lineHeight: 22,
              color: "rgba(247,244,238,0.7)",
            }}
          >
            {t}
          </Text>
        </View>
      ))}
      <Pressable
        onPress={() => {
          // Tag the click before nav so it always lands even if the
          // route swap unmounts us mid-await. RM clicks fire with
          // source=landing_rm_section so we can compare placements;
          // licensing clicks keep the legacy variant-only payload.
          if (variant === "rm") {
            void import("../../lib/analytics").then(({ track }) =>
              track("discovery_call_cta_clicked", { source: "landing_rm_section", variant })
            );
            // Skip Expo Router for RM — go straight to Bookings in a
            // new tab so the user keeps the marketing context behind
            // them while they pick a slot on Tyler's calendar.
            if (Platform.OS === "web" && typeof window !== "undefined") {
              window.open(BOOKINGS_URL, "_blank", "noopener,noreferrer");
            } else {
              void Linking.openURL(BOOKINGS_URL);
            }
            return;
          }
          void import("../../lib/analytics").then(({ track }) =>
            track("discovery_call_cta_clicked", { variant })
          );
          // ctaHref is required at the licensing call site; the RM path
          // returned above. Defensive guard for the optional prop type.
          if (ctaHref) router.push(ctaHref);
        }}
        style={({ hovered, pressed }) => [
          { marginTop: 14, alignSelf: "flex-start" },
          WEB_POINTER,
          Platform.OS === "web" && hovered ? { transform: [{ translateX: 2 }] } : null,
          pressed ? { opacity: 0.85 } : null,
        ]}
      >
        {({ hovered: ctaHover }) => (
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: Platform.OS === "web" && ctaHover ? AMBER_WARM : AMBER,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(181,135,58,0.45)",
              paddingBottom: 2,
            }}
          >
            {ctaLabel}
          </Text>
        )}
      </Pressable>
    </Pressable>
  );
}

function LockedCompetitionCard({
  tag,
  status,
  title,
  meta,
}: {
  tag: string;
  title: string;
  meta: string[];
  status: "active" | "soon";
}) {
  return (
    <Pressable
      onPress={() => router.push("/signup?role=student" as Href)}
      accessibilityRole="link"
      accessibilityLabel={`${title} (sign up to view full brief)`}
      // RN Web exposes `hovered` on Pressable's render-prop and style-fn. We
      // gate every dynamic style on Platform.OS === "web" so native (Expo Go)
      // gets the original static look — RN doesn't have CSS filter / hover
      // primitives anyway.
      style={({ hovered }: { hovered?: boolean }) => {
        const isHover = Platform.OS === "web" && Boolean(hovered);
        return [
          {
            position: "relative",
            borderRadius: 6,
            borderWidth: 1,
            borderColor: isHover ? "rgba(181,135,58,0.65)" : "rgba(181,135,58,0.25)",
            borderLeftWidth: 0,
            overflow: "hidden",
            backgroundColor: FOREST_MID,
            // Smooth animation on web — RN Web passes these through to CSS.
            transitionProperty: "border-color, transform, box-shadow",
            transitionDuration: "320ms",
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
            transform: isHover ? [{ translateY: -1 }] : undefined,
            boxShadow: isHover
              ? "0 14px 30px -16px rgba(181,135,58,0.55), 0 0 0 1px rgba(181,135,58,0.15)"
              : "0 0 0 0 rgba(0,0,0,0)",
          } as unknown as ViewStyle,
        ];
      }}
    >
      {({ hovered }: { hovered?: boolean }) => {
        const isHover = Platform.OS === "web" && Boolean(hovered);
        return (
          <>
            <View
              className="absolute inset-0"
              style={{
                backgroundColor: isHover ? "rgba(19,30,18,0.30)" : "rgba(19,30,18,0.55)",
                transitionProperty: "background-color",
                transitionDuration: "320ms",
              } as unknown as ViewStyle}
              pointerEvents="none"
            />
            <View
              className="absolute bottom-0 left-0 right-0"
              style={{
                height: 2,
                backgroundColor: AMBER,
                opacity: isHover ? 0.7 : 0.35,
                transitionProperty: "opacity",
                transitionDuration: "320ms",
              } as unknown as ViewStyle}
              pointerEvents="none"
            />
            <View
              className="absolute bottom-0 left-0 top-0 w-[52px] items-center justify-center"
              style={{
                zIndex: 3,
                gap: 4,
                backgroundColor: isHover ? "rgba(181,135,58,0.28)" : "rgba(181,135,58,0.15)",
                borderRightWidth: 1,
                borderRightColor: isHover ? "rgba(181,135,58,0.55)" : "rgba(181,135,58,0.3)",
                transitionProperty: "background-color, border-right-color",
                transitionDuration: "320ms",
              } as unknown as ViewStyle}
            >
              <View style={{ width: 20, height: 1, backgroundColor: "rgba(181,135,58,0.4)" }} />
              <Svg width={22} height={26} viewBox="0 0 22 26">
                <Rect x={2} y={11} width={18} height={13} rx={2} fill="rgba(181,135,58,0.18)" stroke="rgba(181,135,58,0.85)" strokeWidth={1.4} />
                <Path d="M6 11V7.5C6 4.46 8.24 2 11 2s5 2.46 5 5.5V11" stroke="rgba(181,135,58,0.85)" strokeWidth={1.4} strokeLinecap="round" fill="none" />
                <Circle cx={11} cy={18} r={2} fill="rgba(181,135,58,0.9)" />
                <Line x1={11} y1={20} x2={11} y2={22} stroke="rgba(181,135,58,0.7)" strokeWidth={1.4} strokeLinecap="round" />
              </Svg>
              <View style={{ width: 20, height: 1, backgroundColor: "rgba(181,135,58,0.4)" }} />
            </View>
            <View
              style={
                {
                  paddingLeft: 64,
                  paddingVertical: 22,
                  paddingRight: 28,
                  zIndex: 1,
                  // Tease animation: locked content is heavily blurred at rest.
                  // On hover it unblurs to a near-readable 0.5px so the visitor
                  // *almost* sees the brief — clearly inviting them to click.
                  // Native (Expo Go) ignores `filter` (no CSS filter primitive).
                  transitionProperty: "filter",
                  transitionDuration: "360ms",
                  transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
                  filter:
                    Platform.OS === "web"
                      ? isHover
                        ? "blur(0.5px)"
                        : "blur(3px)"
                      : undefined,
                } as unknown as ViewStyle
              }
              aria-hidden={Platform.OS === "web" ? true : undefined}
            >
              <View className="mb-1 flex-row items-center justify-between">
                <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.6, letterSpacing: 2.9, textTransform: "uppercase", color: AMBER }}>
                  {tag}
                </Text>
                <View
                  style={{
                    paddingVertical: 3,
                    paddingHorizontal: 9,
                    borderRadius: 20,
                    borderWidth: 1,
                    backgroundColor: status === "active" ? "rgba(127,203,122,0.15)" : "rgba(181,135,58,0.12)",
                    borderColor: status === "active" ? "rgba(127,203,122,0.3)" : "rgba(181,135,58,0.25)",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9.3,
                      letterSpacing: 2.2,
                      textTransform: "uppercase",
                      color: status === "active" ? LIVE_GREEN : AMBER,
                    }}
                  >
                    {status === "active" ? "● Active" : "Opening Soon"}
                  </Text>
                </View>
              </View>
              <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 18.4, color: LINEN, marginBottom: 6 }}>{title}</Text>
              <View className="mb-2 flex-row flex-wrap items-center">
                {meta.map((m, idx) => (
                  <View key={m} className="flex-row items-center">
                    {idx > 0 ? (
                      <View className="mx-3.5 rounded-full" style={{ width: 3, height: 3, backgroundColor: "rgba(237,232,223,0.3)" }} />
                    ) : null}
                    <Text style={{ fontFamily: FONTS.body, fontSize: 11.7, color: "rgba(237,232,223,0.5)" }}>{m}</Text>
                  </View>
                ))}
              </View>
              <View
                className="self-start rounded-full border px-2.5 py-1"
                style={{
                  borderColor: isHover ? "rgba(181,135,58,0.45)" : "rgba(237,232,223,0.12)",
                  gap: 5,
                  flexDirection: "row",
                  alignItems: "center",
                  transitionProperty: "border-color",
                  transitionDuration: "320ms",
                } as unknown as ViewStyle}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 9.6,
                    letterSpacing: 2.2,
                    textTransform: "uppercase",
                    color: isHover ? AMBER : "rgba(237,232,223,0.4)",
                    transitionProperty: "color",
                    transitionDuration: "320ms",
                  } as unknown as TextStyle}
                >
                  Login to view brief & register
                </Text>
              </View>
            </View>
          </>
        );
      }}
    </Pressable>
  );
}
